/**
 * Self-serve unfreeze — modal that lets a user with a frozen (drawdown-
 * breached) account choose between two remediation paths, killing the
 * "contact support" dead-end.
 *
 *   OPTION A · Remove trades — pick specific closed trades to reverse.
 *     Removed trade's PnL reverses against `current_balance`, drawdown
 *     is recomputed server-side, and if the breach is resolved the
 *     account flips back to active. The picker shows biggest losses
 *     first (most useful to remove), and the client projects the
 *     would-be equity and daily/max drawdown live as trades are
 *     ticked so the user knows exactly how many they need to remove
 *     before submitting.
 *
 *   OPTION B · Reset portfolio — nukes every position and trade,
 *     resets `current_balance = starting_balance`, unfreezes.
 *
 * Available to ALL tiers — Individual, MoE, CMA institutional.
 */
import { useEffect, useState, useMemo } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, fmtKES, fmtPct } from "@/lib/api";
import { toast } from "sonner";
import { signTone, toneClassName } from "@/lib/numberColor";
import {
    AlertTriangle, Loader2, RotateCcw, TrendingUp, Check, X, Sparkles,
} from "lucide-react";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

function StatusPill({ ok, children, testid }) {
    return (
        <span
            data-testid={testid}
            className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border rounded ${
                ok
                    ? "text-success border-success/40 bg-success/10"
                    : "text-danger border-danger/40 bg-danger/10"
            }`}
        >
            {children}
        </span>
    );
}

export default function SelfUnfreezeModal({ open, onClose, onUnfrozen }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [submitting, setSubmitting] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [suggestion, setSuggestion] = useState(null);

    async function askAiForSuggestion() {
        setSuggesting(true);
        setSuggestion(null);
        try {
            const { data } = await api.get("/self-unfreeze/suggest");
            setSuggestion(data);
            if (data.suggestable) {
                // Pre-tick exactly the AI's picks.
                setSelected(new Set(data.trade_ids));
                toast.success(
                    `${data.assistant === "obondo" ? "Obondo" : "Kimani"} picked ${data.count} trade${data.count === 1 ? "" : "s"} — click Apply to unfreeze.`
                );
            } else {
                toast.info(data.rationale || "No minimal removal found.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI suggestion failed");
        } finally {
            setSuggesting(false);
        }
    }

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/self-unfreeze/status");
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load unfreeze options");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!open) return;
        setSelected(new Set());
        load();
    }, [open]);

    // Client-side projection: what would the metrics look like if we
    // reversed the currently-selected trades against current_balance?
    // Matches the server-side computation in
    // `routes/self_unfreeze.py::self_unfreeze_remove_trades`.
    const projection = useMemo(() => {
        if (!data) return null;
        const m = data.metrics;
        const trades = data.closed_trades || [];
        const removed = trades.filter((t) => selected.has(t.id));
        const pnlRemoved = removed.reduce((s, t) => s + Number(t.pnl || 0), 0);
        const newBalance = Number(m.current_balance || 0) - pnlRemoved;
        // floating PnL doesn't change (we're only touching closed trades).
        const newEquity = newBalance + Number(m.floating_pnl || 0);
        const dailyStart = Math.max(
            Number(m.starting_balance || 0),
            Number(m.starting_balance || 0), // best-effort — no explicit daily start returned
        );
        // Reuse `starting_balance` as the daily-DD anchor for the projection.
        // The server has the exact `daily_start_balance` — this is a
        // conservative preview that matches or over-estimates DD.
        const dailyDDUsed = Math.max(0, dailyStart - newEquity);
        const maxDDUsed = Math.max(0, Number(m.starting_balance || 0) - newEquity);
        const breached =
            dailyDDUsed > Number(m.daily_drawdown_limit || 0)
            || maxDDUsed > Number(m.max_drawdown_limit || 0);
        return {
            pnlRemoved: Number(pnlRemoved.toFixed(2)),
            newBalance: Number(newBalance.toFixed(2)),
            newEquity:  Number(newEquity.toFixed(2)),
            dailyDDUsed: Number(dailyDDUsed.toFixed(2)),
            maxDDUsed:   Number(maxDDUsed.toFixed(2)),
            breached,
            wouldUnfreeze: !breached && removed.length > 0,
        };
    }, [data, selected]);

    function toggle(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    async function submitRemove() {
        if (selected.size === 0) return;
        setSubmitting(true);
        try {
            const { data: res } = await api.post("/self-unfreeze/remove-trades", {
                trade_ids: [...selected],
            });
            if (res.unfrozen) {
                toast.success(
                    `Account restored — removed ${res.trades_removed} trade${res.trades_removed === 1 ? "" : "s"}`
                );
                onUnfrozen?.();
                onClose();
            } else {
                toast.info(
                    `Removed ${res.trades_removed} trade${res.trades_removed === 1 ? "" : "s"} but still in breach — pick more to remove.`
                );
                setSelected(new Set());
                await load();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Remove failed");
        } finally {
            setSubmitting(false);
        }
    }

    async function submitReset() {
        setSubmitting(true);
        try {
            const { data: res } = await api.post("/self-unfreeze/reset-portfolio");
            toast.success(
                `Portfolio reset to ${fmtKES(res.starting_balance_kes)} — account active`
            );
            setConfirmReset(false);
            onUnfrozen?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
            setConfirmReset(false);
        } finally {
            setSubmitting(false);
        }
    }

    const m = data?.metrics || {};
    const trades = data?.closed_trades || [];
    const { sorted: sortedTrades, sortKey, direction, requestSort } = useSortableData(trades, {
        accessors: {
            symbol: (t) => t.symbol || "",
            side: (t) => t.side || "",
            qty: (t) => Number(t.qty || 0),
            pnl: (t) => Number(t.pnl || 0),
        },
    });

    return (
        <>
            <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
                <DialogContent
                    className="max-w-4xl max-h-[92vh] overflow-y-auto"
                    data-testid="self-unfreeze-modal"
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-danger">
                            <AlertTriangle className="w-5 h-5" /> Reopen your account
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            Your account is frozen because you breached the drawdown limit.
                            Pick one of the two paths below to bring it back to active — no
                            support ticket required. Available to all tiers.
                        </DialogDescription>
                    </DialogHeader>

                    {loading && (
                        <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="unfreeze-loading">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                        </div>
                    )}

                    {!loading && data && (
                        <>
                            {/* Current freeze state summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
                                <SummaryTile label="Starting" value={fmtKES(m.starting_balance)} />
                                <SummaryTile label="Balance"  value={fmtKES(m.current_balance)} />
                                <SummaryTile label="Equity"   value={fmtKES(m.equity)} />
                                <SummaryTile label="Status"   value={<StatusPill ok={false}>{data.account_status?.toUpperCase()}</StatusPill>} />
                                <SummaryTile
                                    label="Daily DD used"
                                    value={`${fmtKES(m.daily_drawdown_used)} / ${fmtKES(m.daily_drawdown_limit)}`}
                                    tone={m.daily_drawdown_used > m.daily_drawdown_limit ? "danger" : "default"}
                                />
                                <SummaryTile
                                    label="Max DD used"
                                    value={`${fmtKES(m.max_drawdown_used)} / ${fmtKES(m.max_drawdown_limit)}`}
                                    tone={m.max_drawdown_used > m.max_drawdown_limit ? "danger" : "default"}
                                />
                                <SummaryTile label="Open positions" value={data.open_positions_count} />
                                <SummaryTile label="Closed trades"  value={trades.length} />
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                {/* ── Option A · Remove trades ────────────────────────── */}
                                <div className="border border-border rounded p-3 bg-card">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">A</span>
                                        <div className="font-semibold text-sm">Remove trades</div>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mb-2">
                                        Pick trades to reverse. Biggest losses first — removing them
                                        adds their loss back to your balance. Keep ticking until the
                                        <span className="mono">"would unfreeze"</span> badge below turns green,
                                        then hit apply.
                                    </div>

                                    {/* AI-recommended minimum removal — one click and we tick the
                                        smallest set of losing trades that clears the breach. */}
                                    <button
                                        type="button"
                                        data-testid="unfreeze-ai-suggest"
                                        onClick={askAiForSuggestion}
                                        disabled={suggesting || submitting || trades.length === 0}
                                        className="w-full mb-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded border border-primary/50 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground text-xs font-bold transition disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {suggesting
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <Sparkles className="w-3 h-3" />}
                                        Ask Kimani / Obondo for the minimum removal
                                    </button>
                                    {suggestion && (
                                        <div
                                            data-testid="unfreeze-ai-rationale"
                                            className={`mb-2 border rounded p-2 text-[11px] ${
                                                suggestion.suggestable
                                                    ? "border-primary/40 bg-primary/5"
                                                    : "border-amber-500/40 bg-amber-500/5 text-amber-700"
                                            }`}
                                        >
                                            <div className="font-semibold mb-0.5 flex items-center gap-1">
                                                <Sparkles className="w-3 h-3" />
                                                {suggestion.assistant === "obondo" ? "Obondo says" : "Kimani says"}
                                            </div>
                                            <div>{suggestion.rationale}</div>
                                        </div>
                                    )}

                                    <div className="border border-border rounded overflow-hidden max-h-[320px] overflow-y-auto mb-2">
                                        <table className="w-full text-xs">
                                            <thead className="bg-secondary/30 text-muted-foreground sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-2 py-1 text-left w-8"></th>
                                                    <SortableTh label="Symbol" sortKey="symbol" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-2 py-1" />
                                                    <SortableTh label="Side" sortKey="side" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-2 py-1" />
                                                    <SortableTh label="Qty" sortKey="qty" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-2 py-1" />
                                                    <SortableTh label="P&L" sortKey="pnl" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-2 py-1" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedTrades.map((t) => {
                                                    const pnl = Number(t.pnl || 0);
                                                    const on = selected.has(t.id);
                                                    return (
                                                        <tr
                                                            key={t.id}
                                                            className={`border-t border-border cursor-pointer hover:bg-secondary/40 ${on ? "bg-primary/10" : ""}`}
                                                            onClick={() => toggle(t.id)}
                                                            data-testid={`unfreeze-trade-row-${t.id}`}
                                                        >
                                                            <td className="px-2 py-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={on}
                                                                    readOnly
                                                                    aria-label="select trade"
                                                                />
                                                            </td>
                                                            <td className="px-2 py-1 mono font-semibold">{t.symbol}</td>
                                                            <td className={`px-2 py-1 uppercase font-bold ${t.side === "buy" ? "text-success" : "text-danger"}`}>{t.side}</td>
                                                            <td className="px-2 py-1 text-right mono">{t.qty}</td>
                                                            <td className={`px-2 py-1 text-right mono ${toneClassName(signTone(pnl))}`}>
                                                                {fmtKES(pnl)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {trades.length === 0 && (
                                                    <tr>
                                                        <td colSpan={5} className="p-4 text-center text-muted-foreground text-xs">
                                                            No closed trades — try the reset option on the right.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Projection panel */}
                                    <div className="border border-border rounded p-2 bg-secondary/30 text-[11px] mb-2 space-y-1">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Selected trades</span>
                                            <span className="mono" data-testid="unfreeze-selected-count">{selected.size}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">PnL that will be reversed</span>
                                            {/* Intentionally NOT the global sign-color convention: this is the
                                                pnl of trades being UNDONE, so a negative value here is the
                                                desired ("success") outcome — business-logic color, not magnitude. */}
                                            <span className={`mono ${projection?.pnlRemoved < 0 ? "text-success" : "text-danger"}`} data-testid="unfreeze-pnl-removed">
                                                {fmtKES(projection?.pnlRemoved || 0)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Projected balance</span>
                                            <span className="mono" data-testid="unfreeze-new-balance">{fmtKES(projection?.newBalance || 0)}</span>
                                        </div>
                                        <div className="flex justify-between items-center pt-1 border-t border-border/50">
                                            <span className="text-muted-foreground">Would unfreeze?</span>
                                            {projection?.wouldUnfreeze
                                                ? <StatusPill ok testid="unfreeze-would-unfreeze"><Check className="inline w-3 h-3 mr-0.5" /> YES</StatusPill>
                                                : <StatusPill ok={false} testid="unfreeze-would-unfreeze">{selected.size === 0 ? "PICK TRADES" : "STILL BREACHED"}</StatusPill>}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        data-testid="unfreeze-apply-btn"
                                        disabled={submitting || selected.size === 0}
                                        onClick={submitRemove}
                                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {submitting
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <TrendingUp className="w-3 h-3" />}
                                        Apply · remove {selected.size} trade{selected.size === 1 ? "" : "s"}
                                    </button>
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        If removal doesn't fully resolve the breach, we'll show you the
                                        updated numbers and let you remove more.
                                    </div>
                                </div>

                                {/* ── Option B · Reset portfolio ──────────────────────── */}
                                <div className="border border-border rounded p-3 bg-card">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold">B</span>
                                        <div className="font-semibold text-sm">Reset portfolio</div>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mb-3">
                                        Start over from scratch. Every open position and closed trade is
                                        wiped, your balance returns to <span className="mono">{fmtKES(m.starting_balance)}</span>,
                                        the daily baseline resets, and your account is reactivated.
                                        This is irreversible — pick this if you want a clean slate.
                                    </div>

                                    <div className="border border-border rounded p-2 bg-secondary/30 text-[11px] mb-3 space-y-1">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Open positions to wipe</span>
                                            <span className="mono">{data.open_positions_count}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Closed trades to wipe</span>
                                            <span className="mono">{trades.length}</span>
                                        </div>
                                        <div className="flex justify-between pt-1 border-t border-border/50">
                                            <span className="text-muted-foreground">Balance after reset</span>
                                            <span className="mono font-bold text-success">{fmtKES(m.starting_balance)}</span>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        data-testid="unfreeze-reset-btn"
                                        disabled={submitting}
                                        onClick={() => setConfirmReset(true)}
                                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded border border-amber-500/50 text-amber-600 hover:bg-amber-500 hover:text-white text-xs font-bold transition disabled:opacity-40"
                                    >
                                        <RotateCcw className="w-3 h-3" /> Reset portfolio & unfreeze
                                    </button>
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        You'll be asked to confirm — no accidental clicks.
                                    </div>
                                </div>
                            </div>

                            <DialogClose asChild>
                                <button
                                    type="button"
                                    data-testid="self-unfreeze-close"
                                    className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 rounded border border-border hover:bg-secondary text-xs font-semibold transition"
                                >
                                    <X className="w-3 h-3" /> Close
                                </button>
                            </DialogClose>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reset confirmation — extra guardrail so a single misclick never
                nukes a user's portfolio. */}
            <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
                <AlertDialogContent data-testid="unfreeze-reset-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset your entire portfolio?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete <strong>{data?.open_positions_count ?? 0} open positions</strong>
                            {" "}and <strong>{trades.length} closed trades</strong>, return your balance to
                            {" "}<strong className="mono">{fmtKES(m.starting_balance)}</strong>, and reactivate your account.
                            {" "}The trades will not appear in your Audit log going forward. This
                            action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="unfreeze-reset-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="unfreeze-reset-confirm-btn"
                            onClick={submitReset}
                            disabled={submitting}
                            className="bg-amber-500 text-white hover:bg-amber-600"
                        >
                            {submitting ? "Resetting…" : "Reset & unfreeze"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

function SummaryTile({ label, value, tone = "default" }) {
    const toneCls = tone === "danger" ? "text-danger" : "";
    return (
        <div className="border border-border rounded p-2 bg-card">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
            <div className={`mono text-sm font-semibold ${toneCls}`}>{value}</div>
        </div>
    );
}
